import { InfoCircleOutlined } from '@ant-design/icons';
import { Skeleton } from 'antd';
import commaNumber from 'comma-number';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import Checkbox from '@/components/Checkbox/Checkbox';
import LazyLoader from '@/components/Common/LazyLoader';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Empty from '@/components/Empty/Empty';
import ExpandSearchInput from '@/components/Inputs/ExpandSearchInput';
import List from '@/components/List/List';
import Modal from '@/components/Modal/Modal';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import { CHART_FREQUENCY } from '@/entities/chart/model/constants';
import { useFeedbackTerminology } from '@/entities/workspace';
import EntireDatePresetFilter from '@/features/Commons/EntireDatePresetFilter';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import taxonomyUtils from '@/features/Taxonomy/utils/taxonomy';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import useTaxonomyStore from '@/stores/TaxonomyStore';
import { radius } from '@/styles/sizes';
import { customScrollbar } from '@/styles/style';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { TAXONOMY_CATEGORY, TOPIC_2_TYPE } from '@/types/taxonomyConst';
import keymap from '@/utils/keymap';
import { getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';

import TaxonomyContext, {
  TopicTreeSortBy,
} from '../../context/TaxonomyContext';
import { useTaxonomyDrag } from '../../context/TaxonomyDragContext';
import { useTopicDetailContext } from '../../context/TopicDetailContext';
import { buildTaxonomyTopicPath } from '../../utils/routes';
import FeedbackCard from '../FeedbackCard/FeedbackCard';
import AISuggestionModal from '../TaxonomyDetail/AISuggestionModal/AISuggestionModal';
import SelectTopicTypeModal from '../TaxonomyDetail/CreateTopicModal/SelectTopicTypeModal';
import AiTopicCreateGuide from './CreateTopicGuide/AiTopicCreateGuide';
import CreateTopicGuide from './CreateTopicGuide/CreateTopicGuide';
import FloatActionMenu from './FloatActionMenu/FloatActionMenu';
import HeaderActions from './Header/HeaderActions';
import TaxonomyHeader from './Header/TaxonomyHeader';
import Topic2Header from './Header/Topic2Header';
import PredictionStateDisplay from './PredictionStateDisplay/PredictionStateDisplay';
import { FeedbackListTarget } from './queries/useTopicDetailQueries';
import SavedTopicGuide from './SavedTopicGuide/SavedTopicGuide';

const SELECT_LIMIT = 1000;

enum SavedTaxonomyStatus {
  NOT_APPLICABLE = 'not_applicable',
  EMPTY = 'empty',
  LOADING = 'loading',
  SUCCESS = 'success',
}

const Topic2Detail = () => {
  const { toDetailPage, openModal } = useGlobalSearchModalAction();
  const addToast = useToast();
  const setPopup = usePopup();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const wr = useWorkspaceRouter();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const location = useLocation();

  const [feedbackModal, setFeedbackModal] = useState<null | {
    feedbackAlias: string;
    blockAlias?: string;
  }>(null);
  const [guideState, setGuideState] = useWorkspaceStorage(
    STORAGE_KEYS.TOPIC_DETAIL_GUIDE_STATE,
    true,
  );
  const { topic_alias } = useParams();
  const { fetchTopics, taxonomy, taxonomyAlias, topicListFetched } =
    useContext(TaxonomyContext);
  const { topicTreeMap, getTaxonomyRootTopics } = useTaxonomyStore((state) => {
    return {
      topicTreeMap: state.topicTreeMap,
      getTaxonomyRootTopics: state.getTaxonomyRootTopics,
    };
  });

  const [savedTaxonomyStatus, setSavedTaxonomyStatus] =
    useState<SavedTaxonomyStatus>(SavedTaxonomyStatus.LOADING);
  const isSettingsRoute = location.pathname.includes('/settings/');
  const detailRootRef = useRef<HTMLDivElement>(null);
  const [floatMenuLeft, setFloatMenuLeft] = useState<number | null>(null);

  const dragContext = useTaxonomyDrag();
  const { selectedList, setSelectedList } = dragContext;
  if (!taxonomyAlias) throw new Error('Invalid params');

  const createModalState = useModalState();
  const aiSuggestionModalState = useModalState();

  const {
    feedbackList,
    count,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
    childTopics,
    filterQueryFormats,
    dateState,
    onDateFilterChange,
    onFilterSetChange,
    clearFilter,
    hasFilter,
    onKeyEnterFeedbackSearch,
    feedbackSearchValue,
    initFeedbacSearchValue,
    selectedTab,
    selectTab,
    isPrediction,
    topic,
    feedbackCount,
    unlinkedFeedbackCount,
    currentPredictionState,
  } = useTopicDetailContext();

  const childTopicType = useMemo(() => {
    return childTopics[0]?.category || null;
  }, [childTopics]);

  const onSelect = (unit: ClassifiedFeedbackUnit) => {
    setSelectedList((prev) => {
      const checkLimit = (newList: ClassifiedFeedbackUnit[]) => {
        const blockCount = newList.reduce((acc, v) => {
          return acc + (v.block_id_list?.length || 0);
        }, 0);
        if (blockCount >= SELECT_LIMIT) {
          addToast('You can select up to 1,000 items.', 'normal');
          return false;
        }
        return true;
      };

      const cardIndex = prev.findIndex(
        (u) => u.feedback_id === unit.feedback_id,
      );

      if (cardIndex === -1) {
        const newList = [...prev, unit];
        if (checkLimit(newList)) return newList;
        return prev;
      }

      if (unit.block_id_list?.length === 0) {
        const newList = [...prev];
        newList.splice(cardIndex, 1);
        return newList;
      }

      const newList = [...prev];
      newList.splice(cardIndex, 1, unit);

      if (checkLimit(newList)) return newList;
      return prev;
    });
  };

  const onSelectAllClick = () => {
    if (selectedList.length > 0) {
      setSelectedList([]);
      return;
    }

    let targetList: ClassifiedFeedbackUnit[] = [];

    let count = 0;
    for (const item of feedbackList || []) {
      const unit: ClassifiedFeedbackUnit = {
        feedback_id: item.alias,
        block_id_list: [],
      };
      for (const block of item.blocks || []) {
        count += 1;
        if (count > SELECT_LIMIT) {
          targetList.push(unit);
          setSelectedList(targetList);
          addToast('You can select up to 1,000 items.', 'normal');
          return;
        }
        unit.block_id_list?.push(block.alias);
      }
      targetList.push(unit);
    }

    const filteredList = targetList.filter(
      (item) => item.block_id_list && item.block_id_list.length > 0,
    );

    setSelectedList(filteredList);
  };

  useEffect(() => {
    const target = detailRootRef.current;
    if (!target) return;

    const updateFloatMenuLeft = () => {
      const rect = target.getBoundingClientRect();
      setFloatMenuLeft(rect.left + rect.width / 2);
    };

    updateFloatMenuLeft();

    const resizeObserver = new ResizeObserver(() => {
      updateFloatMenuLeft();
    });

    resizeObserver.observe(target);
    window.addEventListener('resize', updateFloatMenuLeft);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateFloatMenuLeft);
    };
  }, []);

  const currentBlockCount = useMemo(() => {
    return (
      feedbackList?.reduce((acc, v) => {
        return acc + (v.blocks?.length || 0);
      }, 0) || 0
    );
  }, [feedbackList]);

  const onViewInsightsClick = async () => {
    if (!topic) return;
    track(AMP_KEYS.TAXONOMY_VIEW_INSIGHTS_CLICK, {
      topicName: topic.name,
      taxonomyName: taxonomy?.title,
    });

    const structuredFilter =
      structuredFilterUtils.buildStructuredFilter(filterQueryFormats);
    const res = await service.globalSearch.submitSearch({
      title: topic.name,
      filter: {
        ...structuredFilter,
        [FEEDBACK_COLUMNS.SNIPPET]:
          structuredFilterUtils.buildSingleValue(feedbackSearchValue),
        [FEEDBACK_COLUMNS.DATE_START]: getTimezoneDayjs()
          .subtractVal(12, 'month')
          .toJSON(),
        [FEEDBACK_COLUMNS.DATE_END]: getTimezoneDayjs().toJSON(),
        [FEEDBACK_COLUMNS.TAXONOMY_TOPIC]:
          structuredFilterUtils.buildSingleValue(topic_alias),
      },
      options: {
        trend_frequency: CHART_FREQUENCY.DAY,
      },
    });

    toDetailPage(res.alias);
  };

  const onReclassifyClick = () => {
    setPopup({
      type: 'confirm',
      title: 'Re-classify',
      message: replaceFeedbackTerminology(
        'Are you sure you want to re-classify? This will trigger re-classification on all your outstanding feedback.',
      ),
      popupTheme: 'negative',
      onConfirm: async () => {
        if (topic_alias) {
          await service.taxonomy.reclassifyFeedbackInTopic(topic_alias);
        } else {
          await service.taxonomy.reclassifyFeedbackInTaxonomy(taxonomyAlias);
        }
        addToast('Re-classification is started.', 'success');
      },
    });
  };

  const isSavedTopic = useMemo(() => {
    return topic?.category === TOPIC_2_TYPE.SAVED_TOPIC;
  }, [topic?.category]);

  const isSavedTaxonomy = useMemo(() => {
    return !topic_alias && taxonomy?.category === TAXONOMY_CATEGORY.SAVED_TOPIC;
  }, [taxonomy?.category, topic_alias]);

  const initializeSavedTopic = useCallback(async () => {
    if (isSavedTaxonomy) {
      setSavedTaxonomyStatus(SavedTaxonomyStatus.LOADING);
      if (topicListFetched) {
        try {
          const rootTopics = getTaxonomyRootTopics(
            topicTreeMap,
            taxonomyAlias,
          ).map((t) =>
            taxonomyUtils.getTopicFromTopicTree(topicTreeMap[taxonomyAlias], t),
          );

          const sorted = taxonomyUtils.topicSortFunc(
            rootTopics,
            TopicTreeSortBy.TITLE,
            'desc',
          );

          const topic = sorted[0];

          wr.push(
            buildTaxonomyTopicPath(taxonomyAlias, topic.alias),
          );
          setSavedTaxonomyStatus(SavedTaxonomyStatus.SUCCESS);
        } catch (e) {
          setSavedTaxonomyStatus(SavedTaxonomyStatus.EMPTY);
        }
      }
    } else {
      setSavedTaxonomyStatus(SavedTaxonomyStatus.SUCCESS);
    }
  }, [
    getTaxonomyRootTopics,
    isSavedTaxonomy,
    taxonomyAlias,
    topicListFetched,
    topicTreeMap,
  ]);

  useEffect(() => {
    initializeSavedTopic();
  }, [initializeSavedTopic]);

  return (
    <Transition
      css={css`
        width: 100%;
      `}
      initailInnerState="fallback"
      status={savedTaxonomyStatus !== SavedTaxonomyStatus.LOADING}
      fallback={
        <LazyLoader
          show={savedTaxonomyStatus === SavedTaxonomyStatus.LOADING}
          delay={300}
          loader={
            <CenterFlex height="100%" f={1}>
              <Spinner />
            </CenterFlex>
          }
        />
      }
    >
      {savedTaxonomyStatus === SavedTaxonomyStatus.EMPTY ? (
        <Style j="center" a="center" style={{ height: '100vh' }}>
          <VFlex>
            <Empty
              image={<GuideImage />}
              title="Explore and save topics"
              description="Save and continuously manage Trending or Search results"
              action={{
                text: 'Start exploring',
                onClick: openModal,
              }}
            />
          </VFlex>
        </Style>
      ) : (
        <Style ref={detailRootRef}>
          <ScrollArea g={20} $isSettingsRoute={isSettingsRoute}>
            <Flex j="space-between" g={24} a="flex-start">
              {topic_alias ? (
                <Topic2Header topic={topic} />
              ) : (
                <TaxonomyHeader taxonomy={taxonomy} />
              )}
              {!isPrediction && (
                <HeaderActions
                  level={topic?.level || 0}
                  data={topic}
                  topic_alias={topic_alias}
                  taxonomyAlias={taxonomyAlias}
                  aiSuggestionModalState={aiSuggestionModalState}
                  createModalState={createModalState}
                  childTopicType={childTopicType}
                  refetch={refetch}
                  onViewInsightsClick={onViewInsightsClick}
                />
              )}
            </Flex>

            {typeof currentPredictionState.prediction_state !== 'undefined' && (
              <PredictionStateDisplay
                prediction_state={currentPredictionState.prediction_state}
                progress_rate={currentPredictionState.progress_rate}
              />
            )}

            {
              <Transition
                id="topic"
                status={!isPrediction}
                fallback={<Topic2DetailSkeleton />}
                disableAnimation
              >
                {childTopics.length === 0 &&
                  taxonomyUtils.canCreateChildTopic(topic?.level) &&
                  guideState && (
                    <CreateTopicGuide
                      onAIExploreClick={aiSuggestionModalState.open}
                      onCreateOwnClick={createModalState.open}
                      onCloseClick={() => setGuideState(false)}
                    />
                  )}

                <Space h={24} />

                <VFlex g={20}>
                  <Flex g={16}>
                    <TabWrapper
                      selected={selectedTab === FeedbackListTarget.TOTAL}
                      onClick={() => selectTab(FeedbackListTarget.TOTAL)}
                    >
                      <Heading type="h3" weight="b">
                        {commaNumber(feedbackCount)}
                      </Heading>
                      <Typo type="label2" weight="m" className="text-gray-700">
                        {replaceFeedbackTerminology('All feedback')}
                      </Typo>
                    </TabWrapper>
                    {childTopics.length > 0 && (
                      <TabWrapper
                        selected={selectedTab === FeedbackListTarget.UNLINKED}
                        onClick={() => selectTab(FeedbackListTarget.UNLINKED)}
                      >
                        <Typo>
                          <Heading
                            type="h3"
                            weight="b"
                            style={{ display: 'inline-block' }}
                          >
                            {(
                              (unlinkedFeedbackCount / feedbackCount) *
                              100
                            ).toFixed(0)}
                            %
                          </Heading>
                          &nbsp;&nbsp;
                          <Typo type="label1" weight="sb">
                            ({commaNumber(unlinkedFeedbackCount)} /{' '}
                            {commaNumber(feedbackCount)})
                          </Typo>
                        </Typo>

                        <Typo
                          type="label2"
                          weight="m"
                          className="text-gray-700"
                        >
                          Not classified&nbsp;
                          <Tooltip
                            tooltipNode={replaceFeedbackTerminology(
                              'Feedback that is not classified into existing subtopics.',
                            )}
                          >
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Typo>
                      </TabWrapper>
                    )}
                  </Flex>

                  {selectedTab === FeedbackListTarget.UNLINKED && (
                    <AiTopicCreateGuide
                      reclassifyOnly={childTopicType === TOPIC_2_TYPE.RULE}
                      count={unlinkedFeedbackCount}
                      onAISuggestionClick={aiSuggestionModalState.open}
                      onReclassifyClick={onReclassifyClick}
                    />
                  )}

                  {isSavedTopic && topic && (
                    <SavedTopicGuide
                      topic={topic}
                      onRefreshClick={() => refetch()}
                    />
                  )}

                  <Flex j="space-between" a="start">
                    <Flex f={1} g={16} a="start" style={{ flexWrap: 'wrap' }}>
                      <EntireDatePresetFilter
                        dateState={dateState}
                        setDateState={onDateFilterChange}
                        size="middle"
                        hideRange
                        reverse
                        allowEntirePeriod
                      />
                      <StructuredFilter
                        values={filterQueryFormats}
                        onChange={onFilterSetChange}
                        clearFilter={clearFilter}
                      />
                    </Flex>
                    <ExpandSearchInput
                      key={`topic-detail-search-${topic_alias || taxonomyAlias}-${feedbackSearchValue}`}
                      css={css`
                        background-color: ${(props) =>
                          props.theme.colors.gray100};
                      `}
                      initialValue={feedbackSearchValue}
                      width="300px"
                      onKeyDown={(e) => {
                        if (!keymap.isEnter(e)) return;
                        onKeyEnterFeedbackSearch(e);
                      }}
                      onClearInput={initFeedbacSearchValue}
                      placeholder={replaceFeedbackTerminology(
                        'Find in feedback',
                      )}
                      sizeType="m"
                    />
                  </Flex>
                  <Flex j="space-between">
                    <Flex g={8} a="center" onClick={onSelectAllClick}>
                      <Checkbox
                        checked={
                          selectedList.length > 0 &&
                          selectedList.length === feedbackList?.length
                        }
                        halfCheck={
                          selectedList.length > 0 &&
                          (feedbackList?.length || 0) > selectedList.length
                        }
                      />
                      <Typo type="label2" weight="r">
                        {currentBlockCount > 1000
                          ? replaceFeedbackTerminology(
                              'Select recent 1,000 feedback blocks',
                            )
                          : 'Select all'}
                      </Typo>
                    </Flex>

                    <Flex a="center" g={16}>
                      {hasFilter && (
                        <Typo
                          type="label2"
                          weight="m"
                          className="text-gray-600"
                        >
                          Filtered : {commaNumber(count || 0)}
                        </Typo>
                      )}
                    </Flex>
                  </Flex>
                  <List
                    className="w-full gap-2 min-h-100 pb-250"
                    isLoading={isLoading}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    data={feedbackList}
                    render={(f) => (
                      <FeedbackCard
                        selectedFeedback={feedbackModal}
                        selectedUnit={
                          selectedList.find(
                            (unit) => unit.feedback_id === f.alias,
                          ) || null
                        }
                        setSelectedFeedback={(params) => {
                          if (params) {
                            setNewModal({
                              alias: params.feedbackAlias,
                              type: DETAIL_MODAL_TYPE.FEEDBACK,
                              idx: 0,
                              keys: [params?.blockAlias],
                              options: {
                                focusedBlock: params?.blockAlias,
                                hideTopicTab: true,
                              },
                            });
                            setFeedbackModal(params);
                          } else {
                            setFeedbackModal(null);
                          }
                        }}
                        key={f.alias}
                        feedback={f}
                        onSelect={onSelect}
                        dragOptions={{
                          dragContext,
                          currentTopicAlias: topic_alias,
                          inUnlinkedFeedback:
                            selectedTab === FeedbackListTarget.UNLINKED,
                          selectedList,
                        }}
                      />
                    )}
                  />
                </VFlex>
              </Transition>
            }
          </ScrollArea>
          <Modal
            status={createModalState.status}
            closeModal={() => {
              createModalState.close();
              fetchTopics();
            }}
          >
            <SelectTopicTypeModal
              type={childTopicType}
              target={topic_alias ? 'topic' : 'taxonomy'}
              targetAlias={topic_alias || taxonomyAlias}
              taxonomyAlias={taxonomyAlias}
              targetName={
                topic_alias ? topic?.name || '' : taxonomy?.title || ''
              }
            />
          </Modal>

          <Modal
            status={aiSuggestionModalState.status}
            closeModal={() => {
              aiSuggestionModalState.close();
              fetchTopics();
            }}
          >
            <AISuggestionModal
              target={topic_alias ? 'topic' : 'taxonomy'}
              targetAlias={topic_alias ? topic_alias : taxonomyAlias}
              targetName={
                topic_alias ? topic?.name || '' : taxonomy?.title || ''
              }
              taxonomyAlias={taxonomyAlias}
            />
          </Modal>
        </Style>
      )}
      {createPortal(
        <Transition
          status={selectedList.length > 0 || !!dragContext.isDragging}
        >
          <FloatActionMenuAnchor
            style={{
              left: floatMenuLeft ? `${floatMenuLeft}px` : '50%',
            }}
          >
            <FloatActionMenu
              currentTopicType={topic?.category || null}
              selectedList={selectedList}
              topic_alias={topic_alias}
              taxonomyAlias={taxonomyAlias}
              onClearListClick={() => setSelectedList([])}
              refetchList={refetch}
              selectedCount={selectedList.length}
              isAllSelected={selectedList.length === feedbackList?.length}
              onCheckAllClick={onSelectAllClick}
            />
          </FloatActionMenuAnchor>
        </Transition>,
        document.body,
      )}
    </Transition>
  );
};

const Style = styled(VFlex)`
  position: relative;
  width: 100%;
  background-color: ${(props) => props.theme.colors.gray200};
`;

const FloatActionMenuAnchor = styled.div`
  position: fixed;
  left: 50%;
  bottom: 48px;
  transform: translateX(-50%);
  width: fit-content;
  height: fit-content;
  z-index: 10;
`;

const ScrollArea = styled(VFlex)<{ $isSettingsRoute: boolean }>`
  height: ${({ $isSettingsRoute }) => ($isSettingsRoute ? '100%' : '100vh')};
  padding: 32px 24px 0px 32px;
  overflow: auto;
  ${customScrollbar};
  scrollbar-gutter: stable;
`;

const TabWrapper = styled(VFlex)<{ selected: boolean }>`
  padding: 12px 60px 12px 24px;
  gap: 4px;
  background-color: ${(props) => props.theme.colors.gray100};
  border-radius: ${radius.label}px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  color: ${(props) => props.theme.colors.gray600};
  cursor: pointer;
  transition: all 0.2s;
  ${(props) =>
    props.selected &&
    css`
      border-color: ${(props) => props.theme.colors.primary100};
      color: ${(props) => props.theme.colors.gray800};
    `}
`;

const GuideImage = () => {
  return (
    <img
      src={`/images/saved-topic-empty-image.png`}
      style={{ borderRadius: 16, width: 420 }}
    />
  );
};

const Topic2DetailSkeleton = () => {
  return (
    <VFlex g={20}>
      <Flex g={8}>
        <Skeleton.Node active style={{ width: 200, height: 48 }} />
        <Skeleton.Node active style={{ width: 200, height: 48 }} />
      </Flex>
      <Flex j="space-between">
        <Flex g={8}>
          <Skeleton.Button active />
          <Skeleton.Button active />
        </Flex>
        <Skeleton.Input active />
      </Flex>
      <VFlex g={16}>
        <Skeleton.Node active style={{ width: '100%', height: 200 }} />
        <Skeleton.Node active style={{ width: '100%', height: 160 }} />
        <Skeleton.Node active style={{ width: '100%', height: 120 }} />
        <Skeleton.Node active style={{ width: '100%', height: 240 }} />
      </VFlex>
    </VFlex>
  );
};

export default Topic2Detail;
